import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Dialog, DialogTitle, IconButton } from "@mui/material";
import { styled } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import TextView from "../TextView";
import {
  setReportInstance,
  setReportLocation,
  setReportModalOpen,
} from "../../redux/actions";
import { Form, Formik } from "formik";
import {
  REPORT_LOCATION_MAP,
  reportCategories,
  reportModalInitialValues,
  reportModalValidationSchema,
} from "./ReportModalHelpers";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import FinalReportForm from "./children/FinalReportForm";
import ReportOptions from "./children/ReportOptions";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "react-toastify";
import { useSubmitReport } from "../../services/mutations";

const StyledDialog = styled(Dialog)(({ theme }) => ({
  ".MuiBackdrop-root": {
    backgroundColor: "#00000066",
  },
  ".MuiPaper-root": {
    padding: "24px",
    border: "1px solid #E1E1E1",
    borderRadius: "8px",
    width: "660px",
    maxWidth: "100%",
  },

  [theme.breakpoints.down("sm")]: {
    width: "90vw",
  },
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "0px",
  position: "relative",
}));

const ReportModal = () => {
  const dispatch = useDispatch();

  const [path, setPath] = useState([]);
  const [apiLoading, setApiLoading] = useState(false);

  const { reportModalOpen, reportInstance, reportLocation } = useSelector(
    (state) => state.userProfile
  );

  const submitReport = useSubmitReport();

  const handleCancel = (resetForm) => {
    dispatch(setReportModalOpen(false));
    dispatch(setReportInstance(null));
    dispatch(setReportLocation(null));
    setPath([]);
    if (resetForm) {
      resetForm();
    }
  };

  const handleSelectOption = (option) => {
    setPath((prev) => [...prev, option]);
  };

  const goBack = () => {
    // Remove the last selected option from the path
    setPath((prev) => prev.slice(0, -1));
  };

  const getCurrentOptions = () => {
    if (path.length === 0) {
      return reportCategories;
    }
    const currentNode = path[path.length - 1];
    return currentNode.subcategories || [];
  };

  const isAtLeafNode = () => {
    if (path.length === 0) return false;
    const currentNode = path[path.length - 1];

    return (
      !currentNode?.subcategories || currentNode.subcategories.length === 0
    );
  };

  const onSubmitHandler = async (values, formikHelpers) => {
    formikHelpers.setSubmitting(true);
    setApiLoading(true);

    const payload = {
      instance: reportInstance,
      report_location: REPORT_LOCATION_MAP[reportLocation] || null,
      cat_slug: path[path.length - 1]?.id,
      reason: values.details,
    };

    submitReport.mutate(payload, {
      onSuccess: (response) => {
        formikHelpers.setSubmitting(false);
        formikHelpers.resetForm();
        handleCancel();
        setApiLoading(false);
        toast.success("Report submitted successfully!");
      },
      onError: (err) => {
        formikHelpers.setSubmitting(false);
        setApiLoading(false);
        toast.error("Some error occurred!");
      },
    });
  };

  const renderContent = () => {
    const options = getCurrentOptions();
    const variants = {
      enter: { opacity: 0, scale: 0.95 }, // Start slightly scaled down and invisible
      center: { opacity: 1, scale: 1 }, // Fully visible and normal size
      exit: { opacity: 0, scale: 0.95 }, // Fade out and shrink slightly
    };

    return (
      <AnimatePresence mode="wait">
        <motion.div
          key={path.map((node) => node.id).join("-")} // Unique key for each path
          variants={variants}
          initial="enter"
          animate="center"
          exit="exit"
          transition={{ duration: 0.3 }} // Smooth fade-in/out
          style={{
            padding: "0px",
            marginTop: "20px",
            display: "flex",
            flexDirection: "column",
            gap: "20px",
          }}
        >
          {isAtLeafNode() ? (
            <FinalReportForm path={path} apiLoading={apiLoading} />
          ) : (
            <ReportOptions
              options={options}
              onSelectOption={handleSelectOption}
              path={path}
            />
          )}
        </motion.div>
      </AnimatePresence>
    );
  };

  return (
    <Formik
      initialValues={reportModalInitialValues}
      validationSchema={reportModalValidationSchema}
      onSubmit={onSubmitHandler}
    >
      {({ errors, touched, resetForm }) => (
        <StyledDialog
          open={reportModalOpen}
          onClose={() => handleCancel(resetForm)}
          BackdropProps={{
            timeout: 500,
          }}
        >
          <StyledDialogTitle>
            {path.length > 0 && (
              <IconButton
                onClick={goBack}
                sx={{
                  color: "#1E232C",
                  position: "absolute",
                  left: 0,
                  top: -8,
                  border: "1px solid #E8ECF4",
                  borderRadius: "12px",
                }}
              >
                <ChevronLeftIcon />
              </IconButton>
            )}

            <TextView variant="largeTitle">
              <span style={{ color: "#0F0F0F" }}>Report</span>
            </TextView>

            <IconButton
              aria-label="close"
              onClick={() => handleCancel(resetForm)}
              sx={{
                color: "#123051",
                position: "absolute",
                right: 0,
                top: -8,
              }}
            >
              <CloseIcon />
            </IconButton>
          </StyledDialogTitle>

          <Form>{renderContent()}</Form>
        </StyledDialog>
      )}
    </Formik>
  );
};

export default ReportModal;
